import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import Http from "../../../../Common/Http";
import IsLogin from "../../../../Common/IsLogin";
import { exportToCSV } from "../../../../Common/ExportUtils";

// Local date (YYYY-MM-DD). toISOString() uses UTC, which gives the wrong
// day for Dhaka between 00:00 and 06:00.
const getToday = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const today = getToday();

// "2026-11-04" -> "04 Nov"
const formatDateHeader = (date) => {
  const parsed = new Date(date + "T00:00:00");
  if (isNaN(parsed)) return date;
  return parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

// "2026-11-04 10:24:33" -> "10:24 AM"
const formatTime = (value) => {
  if (!value) return "";
  const str = value.toString().trim();
  const parts = str.split(/\s+/);
  const timePart = parts.length > 1 ? parts[1] : parts[0];

  const t = timePart.split(":");
  if (t.length < 2) return timePart;

  let h = parseInt(t[0], 10);
  const m = t[1];

  if (isNaN(h)) return timePart;

  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = h.toString().padStart(2, "0");
  const mm = m.toString().padStart(2, "0");

  return `${hh}:${mm} ${ampm}`;
};

const getAttendanceData = (rowData, date) => {
  if (!rowData || !rowData.attendance) return null;
  // Array structure (also covers an empty PHP array serialized as [])
  if (Array.isArray(rowData.attendance)) {
    return rowData.attendance.find((a) => a?.attendance_at?.startsWith(date)) || null;
  }
  // Object structure: rowData.attendance["2026-11-04"]
  return rowData.attendance[date] || null;
};

const renderDevMode = (record) => {
  if (!record) return "";
  return Number(record.developer_mode_on) === 1 ? "On" : "Off";
};

const DeveloperModeAttendance = () => {
  const [inputs, setInputs] = useState({ from: today, to: today });
  const [applied, setApplied] = useState({ from: today, to: today });
  const [dates, setDates] = useState([]);
  const [receiveData, setReceiveData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const fetchData = async (filters = applied) => {
    if (!filters.from || !filters.to) return;
    setLoading(true);
    try {
      const res = await Http.get(
        `/report/developer-mode-attendance?from_date=${filters.from}&to_date=${filters.to}`,
      );
      setDates(res.data.dates || []);
      setReceiveData(res.data.list || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formSubmit = () => {
    setApplied(inputs);
    fetchData(inputs);
  };

  const handleExportCSV = () => {
    setExporting(true);
    try {
      const exportCols = [
        { header: "Card No", field: "card_no" },
        { header: "Name", field: "name" },
        { header: "Designation", field: "designation" },
        { header: "Workplace", field: "workplace" },
        { header: "Email", field: "email" },
        { header: "Phone No", field: "mobile" },
        ...dates.flatMap((date) => [
          {
            header: `${date} Attendance`,
            accessor: (row) => formatTime(getAttendanceData(row, date)?.attendance_at),
          },
          {
            header: `${date} Developer Mode`,
            accessor: (row) => renderDevMode(getAttendanceData(row, date)),
          },
        ]),
      ];

      exportToCSV({
        cols: exportCols,
        data: receiveData,
        filename: `developer_mode_attendance_${applied.from}_${applied.to}`,
      });
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExporting(false);
    }
  };

  // Each date becomes a group of two columns: Attendance and Dev Mode.
  // NOTE: flatMap (not map) so the Columns are direct children, not nested arrays.
  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Card No" rowSpan={2} />
        <Column header="Name" rowSpan={2} />
        <Column header="Designation" rowSpan={2} />
        <Column header="Workplace" rowSpan={2} />
        <Column header="Email" rowSpan={2} />
        <Column header="Phone No" rowSpan={2} />
        {dates.map((date) => (
          <Column key={date} header={formatDateHeader(date)} colSpan={2} />
        ))}
      </Row>
      <Row>
        {dates.flatMap((date) => [
          <Column key={`${date}-att-head`} header="Attendance" />,
          <Column key={`${date}-dev-head`} header="Dev Mode" />,
        ])}
      </Row>
    </ColumnGroup>
  );

  // Dynamic body columns, flattened.
  const dateColumns = dates.flatMap((date) => [
    <Column
      key={`${date}-att`}
      columnKey={`${date}-att`}
      header="Attendance"
      className="text-center"
      body={(rowData) => formatTime(getAttendanceData(rowData, date)?.attendance_at)}
    />,
    <Column
      key={`${date}-dev`}
      columnKey={`${date}-dev`}
      header="Dev Mode"
      className="text-center"
      body={(rowData) => renderDevMode(getAttendanceData(rowData, date))}
    />,
  ]);

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Developer Mode On Attendance Report</h5>
        </div>
        <div className="am-pagebody p-2">
          <div className="card pd-sm-40">
            <div className="row">
              <div className="col-lg">
                <label htmlFor="">From Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="from"
                  value={inputs.from || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="col-lg">
                <label htmlFor="">To Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="to"
                  value={inputs.to || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="col-lg mt-4">
                <button
                  className="btn btn-primary btn-block mg-b-10"
                  onClick={formSubmit}
                >
                  <i className="fa fa-send mg-r-10"></i> Search
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="am-pagebody p-2">
          <div className="card pd-sm-40">
            <div className="row">
              <div className="col-lg-12 mb-3">
                <button
                  className="btn btn-success"
                  onClick={handleExportCSV}
                  disabled={exporting || receiveData.length === 0}
                >
                  {exporting ? (
                    <i className="fa fa-spinner fa-spin mg-r-6"></i>
                  ) : (
                    <i className="fa fa-file-excel-o mg-r-6"></i>
                  )}
                  CSV
                </button>
              </div>
              <div className="col-lg-12">
                {/* key forces a remount when the date range changes, so
                    PrimeReact rebuilds its column list */}
                <DataTable
                  key={dates.join(",")}
                  value={receiveData}
                  loading={loading}
                  headerColumnGroup={dates.length ? headerGroup : undefined}
                  paginator
                  rows={20}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  stripedRows
                  showGridlines
                  scrollable
                  scrollHeight="500px"
                  emptyMessage="No Data Found !"
                >
                  <Column columnKey="card_no" field="card_no" header="Card No" />
                  <Column columnKey="name" field="name" header="Name" />
                  <Column columnKey="designation" field="designation" header="Designation" />
                  <Column columnKey="workplace" field="workplace" header="Workplace" />
                  <Column columnKey="email" field="email" header="Email" />
                  <Column columnKey="mobile" field="mobile" header="Phone No" />
                  {dateColumns}
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperModeAttendance;